import { MoreVertical, Eye, Edit, Link, FolderOpen, Calculator, Archive } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { Project } from '@/types/crm';
import { getProjectUrl } from '@/lib/urlUtils';

interface ProjectQuickActionsProps {
  project: Project;
  onEdit: (project: Project) => void;
  onDelete: (id: string) => Promise<void>;
}

export function ProjectQuickActions({ project, onEdit, onDelete }: ProjectQuickActionsProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleCopyLink = (e: React.MouseEvent) => {
    e.stopPropagation();
    const url = getProjectUrl(project.id);
    navigator.clipboard.writeText(url);
    toast({
      title: 'Link gekopieerd',
      description: 'De projectlink is naar je klembord gekopieerd.',
    });
  };

  const handleOpenSharePoint = (e: React.MouseEvent) => {
    e.stopPropagation();
    const sharepointUrl = project.sharepoint_web_url ?? project.sharepoint_main_url;
    if (sharepointUrl) {
      window.open(sharepointUrl, '_blank');
    } else {
      toast({
        title: 'Geen SharePoint map',
        description: 'Dit project heeft nog geen SharePoint map gekoppeld.',
        variant: 'destructive',
      });
    }
  };

  const handleOpenBusinesscase = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/crm/projects/${project.id}/businesscase`);
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await onDelete(project.id);
      setShowDeleteDialog(false);
    } catch (error) {
      console.error('Error deleting project:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <MoreVertical className="h-4 w-4" />
            <span className="sr-only">Acties</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/crm/projects/${project.id}`); }}>
            <Eye className="h-4 w-4 mr-2" />
            Bekijken
          </DropdownMenuItem>
          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit(project); }}>
            <Edit className="h-4 w-4 mr-2" />
            Bewerken
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleCopyLink}>
            <Link className="h-4 w-4 mr-2" />
            Kopieer link
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleOpenSharePoint} disabled={!(project.sharepoint_web_url ?? project.sharepoint_main_url)}>
            <FolderOpen className="h-4 w-4 mr-2" />
            SharePoint map
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleOpenBusinesscase}>
            <Calculator className="h-4 w-4 mr-2" />
            Businesscase
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem 
            onClick={(e) => { e.stopPropagation(); setShowDeleteDialog(true); }}
          >
            <Archive className="h-4 w-4 mr-2" />
            Archiveren
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>Project archiveren</AlertDialogTitle>
            <AlertDialogDescription>
              "{project.name}" verdwijnt uit de lijst maar blijft bewaard. Je kunt
              het later terughalen via het archief.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? 'Archiveren...' : 'Archiveren'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
